import { FC } from "react";
import { styled } from "styled-components";
import { buttonReset, colors, font } from "../lib/sharedStyles";
import { ContentfulNavigationRow } from "../lib/contentfulTypes";
import DesktopMenuLink from "./DesktopMenuLink";
import { SwiperCarousel, SwiperSlide } from "./Swiper";

const NavigationRow = styled.div`
  width: 100%;
  padding: 32px 0 24px;
  border-top: 1px solid ${colors.fender.grey03};

  swiper-slide {
    width: auto;
  }
`;

const NavigationRowHeading = styled.div`
  ${font('Instrument Sans', 600, '12px', '18px', '0.12px')};
  color: ${colors.fender.primaryDark};
`;

const NavRowLogo = styled.div`
  display: flex;
  max-width: 98px;
  margin: 0 0 10px;
`;

const CarouselHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 0 16px 32px;
  
  @media (min-width: 1150px) {
    max-width: 90%;
    margin: 0 auto 16px;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  overflow: visible;
  margin-left: 32px;
  
  @media (min-width: 1150px) {
    max-width: 90%;
    margin: 0 auto;
  }

  &:hover {
    button:not(.swiper-button-disabled) {
      opacity: 1;
    }
  }
`;

const CarouselButton = styled.button`
  ${buttonReset()};
  width: 32px;
  height: 32px;
  transition: background-color 0.2s ease, border-radius 0.2s ease;
  cursor: pointer;
  position: absolute;
  z-index: 10;
  top: 76px;
  background-color: #fff;
  border-radius: 100%;
  opacity: 0;

  svg {
    vertical-align: middle;
  }

  &:first-of-type {
    left: 32px;
  }

  &:not(:first-of-type) {
    right: 32px;

    // Rotate "next" arrow
    svg {
      transform: rotate(180deg);
    }
  }

  // This class is dynamically added by Swiper.
  &.swiper-button-disabled {
    opacity: 0;
    pointer-events: none;

    svg path {
      fill: #E5E5E5;
    }
  }
`;

interface Props extends ContentfulNavigationRow {
  brand: string;
}

const DesktopNavigationRow: FC<Props> = ({
  brand,
  sys,
  fields: {
    logo,
    navRowTitle,
    subItems
  }
}) => {
  const carouselArrows = {
    prevEl: `.swiper-button-prev-${sys.id}`,
    nextEl: `.swiper-button-next-${sys.id}`,
  };

  return (
    <NavigationRow>
      <CarouselHeader>
        <div>
          {(logo) && (
            <NavRowLogo>
              <img src={`https:${logo.fields.file?.url}`} />
            </NavRowLogo>
          )}
          <NavigationRowHeading dangerouslySetInnerHTML={{__html: navRowTitle}} />
        </div>
      </CarouselHeader>
      <CarouselContainer>
        <SwiperCarousel
          slidesPerView="auto"
          spaceBetween={16}
          navigation={carouselArrows}
          mousewheel={{ forceToAxis: true }}
        >
          {subItems?.map(subItem => {
            // This helps hide things needed for mobile, like "Shop All."
            if (subItem.fields.hasOwnProperty('asset') || subItem.fields.productImageUrl) {
              return (
                <SwiperSlide key={subItem.sys.id}>
                  <DesktopMenuLink 
                    brand={brand} 
                    inNavRow 
                    item={subItem} 
                    level={3} 
                  />
                </SwiperSlide>
              );
            }
          })}
        </SwiperCarousel>
        {Object.keys(carouselArrows).map(key => (
          // @ts-ignore untyped object, substring to remove leading "."
          <CarouselButton key={key} className={carouselArrows[key].substring(1)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path fill='#191A1F' d="M11 6.988a.75.75 0 0 0 0-1.5v1.5ZM.47 5.708a.75.75 0 0 0 0 1.06l4.773 4.774a.75.75 0 1 0 1.06-1.061L2.061 6.238l4.242-4.242A.75.75 0 0 0 5.243.935L.47 5.708ZM11 5.488H1v1.5h10v-1.5Z"/></svg>
          </CarouselButton>
        ))}
      </CarouselContainer>
    </NavigationRow>
  );
}

export default DesktopNavigationRow;